import {
  ElementType,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { ArrowUpDown, ChevronDownIcon } from 'lucide-react'
import { NumericFormat } from 'react-number-format'
import { usePrevious } from 'react-use'
import { formatUnits } from 'viem'

import {
  CurrencyAmount,
  TokenDisplay,
  Tooltip,
} from '~/components/component-library'
import { Button } from '~/components/component-library/Button'
import { buildNumberFormatIsAllowed } from '~/components/component-library/NumberInput'
import { formatAbbreviatedTokenAmount } from '~/components/component-library/TokenBalance'
import { TokenSelectorPopover } from '~/components/component-library/TokenSelector/TokenSelectorPopover'
import { getExceedsBalance } from '~/components/component-library/TokenSelector/getExceedsBalance'
import {
  Denomination,
  SortBy,
} from '~/components/component-library/TokenSelector/types'
import { useDenominationInput } from '~/components/component-library/TokenSelector/useDenominationInput'
import {
  SelectedToken,
  useSelectedAggregatedBalance,
} from '~/components/component-library/TokenSelector/useSelectedAggregatedBalance'
import { Input } from '~/components/inputs/Input'
import { ADDRESS_ZERO, EURC_TOKENS } from '~/constants/addresses'
import { useTokenMetadata } from '~/hooks/useTokenMetadata'
import { IAddress, VmType } from '~/types'
import { Address } from '~/utils/address'
import {
  ALLOWED_DECIMAL_SEPARATORS,
  getNumberFromBigNumber,
} from '~/utils/numbers'
import { cn } from '~/utils/tailwind-utils'
import { sendTrackingData } from '~/utils/tracking'
import { isVaultToken } from '~/utils/vault'

// Renders the child as-is — used in the disabled state (no popover trigger).
const PassthroughTrigger = ({
  children,
}: {
  asChild?: boolean
  children: ReactNode
}) => <>{children}</>

type TokenSelectorWithAmountProps = {
  token: SelectedToken
  onTokenChange: (token: NonNullable<SelectedToken>) => void
  amount: string
  onAmountChange: (amount: string) => void
  onAmountFocus?: () => void
  placeholder?: string
  filterAccountAddress?: IAddress
  showBalances: boolean
  denomination: Denomination
  allowRelayNetworks?: boolean
  tokenFilter?: (token: { address: string; chainId: number }) => boolean
  strictTokenFilter?: boolean
  chainFilter?: (chainId: number) => boolean
  disabled?: boolean
  disabledReason?: string
  isLoading?: boolean
  onExceedsBalanceChange?: (exceeds: boolean) => void
  vmTypes?: VmType[]
  /** Tokens to always show first, regardless of balance or frequency */
  promotedTokens?: { address: string; chainId: number }[]
  balanceOverride?: {
    amount: bigint
    totalUsdValue: number
  }
  sortBy: SortBy
}

export function TokenSelectorWithAmount({
  token,
  onTokenChange,
  amount,
  onAmountChange,
  onAmountFocus,
  placeholder = 'Select a token',
  filterAccountAddress,
  showBalances,
  denomination,
  allowRelayNetworks,
  tokenFilter,
  strictTokenFilter,
  chainFilter,
  disabled,
  disabledReason,
  isLoading,
  onExceedsBalanceChange,
  vmTypes,
  promotedTokens,
  balanceOverride,
  sortBy,
}: TokenSelectorWithAmountProps) {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const tokenButtonRef = useRef<HTMLButtonElement>(null)

  const tokenMetadata = useTokenMetadata({
    address: (token?.address ?? ADDRESS_ZERO) as IAddress,
    chainId: token?.chainId ?? 1,
    includePrice: true,
  })

  const { balance: aggregatedBalance, isExternalChain } =
    useSelectedAggregatedBalance({
      token,
      filterAccountAddress,
    })

  const selectedBalance = useMemo(() => {
    if (!balanceOverride || !tokenMetadata) return aggregatedBalance
    return {
      amount: balanceOverride.amount,
      totalUsdValue: balanceOverride.totalUsdValue,
      tokenMetadata: { decimals: tokenMetadata.decimals },
    }
  }, [balanceOverride, aggregatedBalance, tokenMetadata])

  const hasPrice = !!token && !!tokenMetadata?.price
  const decimals =
    tokenMetadata?.decimals ?? selectedBalance?.tokenMetadata.decimals ?? 18

  const isEurc =
    !!token &&
    token.address.toLowerCase() ===
      EURC_TOKENS[token.chainId as keyof typeof EURC_TOKENS]?.toLowerCase()

  const isVaultPosition = !!token && isVaultToken(token.chainId, token.address)

  const {
    displayValue,
    isUsdMode,
    showToggle,
    handleValueChange,
    handleToggleMode,
    setDisplayFromTokenAmount,
    convertedDisplay,
    prefix,
    placeholder: inputPlaceholder,
    decimalScale,
    thousandSeparator,
    decimalSeparator,
  } = useDenominationInput({
    amount,
    onAmountChange,
    denomination,
    tokenAddress: token?.address,
    tokenChainId: token?.chainId,
    tokenPrice: hasPrice ? tokenMetadata?.price?.toString() : undefined,
    tokenSymbol: tokenMetadata?.symbol,
    decimals,
    isEurc,
    isVaultPosition,
    selectedBalanceAmount: selectedBalance?.amount,
  })

  const setAmountToMax = useCallback(() => {
    if (!selectedBalance) return
    const tokenDecimals = selectedBalance.tokenMetadata.decimals
    setDisplayFromTokenAmount(
      formatUnits(selectedBalance.amount, tokenDecimals),
      { lock: true },
    )
  }, [selectedBalance, setDisplayFromTokenAmount])

  const exceedsBalance = useMemo(
    () => getExceedsBalance({ selectedBalance, amount, decimals, sortBy }),
    [selectedBalance, amount, decimals, sortBy],
  )

  const formattedTokenBalance =
    selectedBalance && token ? (
      <>
        {formatAbbreviatedTokenAmount(
          getNumberFromBigNumber(selectedBalance.amount, decimals),
          { address: token.address, chainId: token.chainId },
          { removeTrailingZeros: true },
        )}{' '}
        <TokenDisplay
          layout="inline-text"
          address={Address.format(token.address)}
          chainId={token.chainId}
        />
      </>
    ) : null

  const showBalancePresets =
    showBalances &&
    !!token &&
    !!selectedBalance &&
    !isLoading &&
    // A token on a network the org turned off can't be spent — no MAX to offer.
    // Earn (balanceOverride) is genuinely spendable.
    (!isExternalChain || !!balanceOverride) &&
    (!disabled || !!balanceOverride)

  const showBalanceFooter =
    showBalances && !!token && !!selectedBalance && sortBy === 'value'
  const showDenominationFooter =
    hasPrice && !isEurc && (showToggle || !!convertedDisplay)

  const prevExceeds = usePrevious(exceedsBalance)
  useEffect(() => {
    if (exceedsBalance !== prevExceeds) {
      onExceedsBalanceChange?.(exceedsBalance)
    }
  }, [exceedsBalance, prevExceeds, onExceedsBalanceChange])

  const tokenTriggerButton = (
    <button
      ref={tokenButtonRef}
      type="button"
      disabled={disabled}
      className={cn(
        '-ml-px flex max-w-56 items-center gap-2 whitespace-nowrap rounded-lg rounded-l-none px-3 py-2 outline-none ring-1 ring-inset ring-gray-200',
        'focus-visible:relative focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-blue-500',
        'dark:ring-gray-700',
        disabled
          ? 'cursor-not-allowed bg-gray-100 dark:bg-gray-800'
          : 'bg-white hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800',
      )}
    >
      {token ? (
        <TokenDisplay
          address={token.address as `0x${string}`}
          chainId={token.chainId}
          layout="standard"
          size="base"
          hideInactiveChainIndicator={isExternalChain}
        />
      ) : (
        <span className="text-placeholder">{placeholder}</span>
      )}
      {!disabled && (
        <ChevronDownIcon
          className={cn(
            'size-4 shrink-0',
            token ? 'text-secondary' : 'text-placeholder',
          )}
        />
      )}
    </button>
  )

  const inputContent = (Trigger: ElementType) => (
    <div className="flex w-full">
      <div className="group/amount relative min-w-0 flex-1">
        <NumericFormat
          customInput={Input}
          value={displayValue}
          onValueChange={(values, sourceInfo) => {
            // Only react to user input; ignore programmatic prop updates.
            if (sourceInfo.source !== 'event') return
            handleValueChange(values.value)
          }}
          onFocus={onAmountFocus}
          isAllowed={buildNumberFormatIsAllowed}
          decimalScale={decimalScale}
          prefix={prefix}
          placeholder={inputPlaceholder}
          className={cn('w-full', {
            // Reserve room for the MAX button only when it can actually appear.
            'group-focus-within/amount:pr-16 group-hover/amount:pr-16':
              showBalancePresets,
          })}
          containerClassName="rounded-r-none"
          allowNegative={false}
          thousandSeparator={thousandSeparator}
          decimalSeparator={decimalSeparator}
          allowedDecimalSeparators={ALLOWED_DECIMAL_SEPARATORS}
          isLoading={isLoading}
          error={exceedsBalance ? 'Insufficient balance' : undefined}
          showError={false}
        />
        {showBalancePresets && (
          <div className="absolute right-1.5 top-1/2 flex -translate-y-1/2 items-center gap-1 opacity-0 transition-opacity group-focus-within/amount:opacity-100 group-hover/amount:opacity-100">
            <Button
              type="button"
              variant="secondary"
              size="small"
              className="rounded-md"
              onClick={(e) => {
                e.stopPropagation()
                onAmountFocus?.()
                setAmountToMax()
                sendTrackingData('tokenSelectorPresetClicked', {
                  preset: 'max',
                  tokenAddress: token?.address,
                  chainId: token?.chainId,
                })
              }}
            >
              MAX
            </Button>
          </div>
        )}
      </div>
      <Trigger asChild>
        {disabled && disabledReason ? (
          <Tooltip content={disabledReason}>{tokenTriggerButton}</Tooltip>
        ) : (
          tokenTriggerButton
        )}
      </Trigger>
    </div>
  )

  return (
    <div className="w-full">
      {disabled ? (
        inputContent(PassthroughTrigger)
      ) : (
        <TokenSelectorPopover
          onChange={onTokenChange}
          filterAccountAddress={filterAccountAddress}
          open={popoverOpen}
          onOpenChange={setPopoverOpen}
          showBalances={showBalances}
          allowRelayNetworks={allowRelayNetworks}
          tokenFilter={tokenFilter}
          strictTokenFilter={strictTokenFilter}
          chainFilter={chainFilter}
          vmTypes={vmTypes}
          promotedTokens={promotedTokens}
          sortBy={sortBy}
          restoreTriggerFocusOnClose
          triggerRef={tokenButtonRef}
        >
          {inputContent}
        </TokenSelectorPopover>
      )}

      {/* Footer: denomination toggle + balance. Balance readout hides when
          sortBy='frequency' — balance isn't the primary signal there. */}
      {(showBalanceFooter || showDenominationFooter || exceedsBalance) && (
        <div className="mt-1.5 flex h-5 select-none items-center justify-between text-sm">
          {exceedsBalance && selectedBalance && token ? (
            <span className="text-error">
              Max{' '}
              {isUsdMode ? (
                <CurrencyAmount
                  layout="inline"
                  value={selectedBalance.totalUsdValue}
                />
              ) : (
                formattedTokenBalance
              )}
            </span>
          ) : showDenominationFooter ? (
            showToggle ? (
              <button
                type="button"
                onClick={handleToggleMode}
                className="flex items-center gap-1 rounded-md text-secondary outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                <ArrowUpDown className="size-3" />
                <span>
                  {convertedDisplay ??
                    (isUsdMode ? `0 ${tokenMetadata?.symbol ?? ''}` : '$0.00')}
                </span>
              </button>
            ) : (
              <span className="flex items-center gap-1 text-secondary">
                {convertedDisplay}
              </span>
            )
          ) : (
            <span />
          )}

          {showBalanceFooter && selectedBalance && (
            <span className="text-secondary">
              Balance:{' '}
              {isUsdMode ? (
                <CurrencyAmount
                  value={selectedBalance.totalUsdValue}
                  layout="inline"
                />
              ) : (
                formattedTokenBalance
              )}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
